import React, { createContext, useState } from "react";
import { AuditStats, AuditRecord, NodeRosterIdMappingInfo, PlanGroupSummary } from "../admin-api/generated-src";
import { OVERALL_SEGMENT_ID } from "../constants";

interface auditStateProps {
  currentRosterId: string | undefined;
  auditRecordList: AuditRecord[];
  auditStats: AuditStats;
  rosterIdList: NodeRosterIdMappingInfo[],
  isAuditTableLoading: boolean,
  flipRefresh: boolean;
  planGroups: PlanGroupSummary[];
  selectedPlanGroupId: string | undefined;
  selectedSegmentId: string;
  setCurrentRosterId(rosterId: string | undefined): any;
  setAuditRecordList(auditRecordList: AuditRecord[]): any;
  setAuditStats(auditStats: AuditStats): any;
  setRosterIdList(rosterIdList: NodeRosterIdMappingInfo[]): any;
  setIsAuditTableLoading(isAuditTableLoading: boolean): any;
  setFlipRefresh(): any;
  setPlanGroups(planGroups: PlanGroupSummary[]): void;
  setSelectedPlanGroupId(id: string | undefined): void;
  setSelectedSegmentId(id: string): void;
}

export const initialState: auditStateProps = {
  currentRosterId: undefined,
  auditRecordList: [],
  auditStats: {},
  rosterIdList: [],
  isAuditTableLoading: false,
  flipRefresh: false,
  planGroups: [],
  selectedPlanGroupId: undefined,
  selectedSegmentId: OVERALL_SEGMENT_ID,
  setCurrentRosterId: () => { },
  setAuditRecordList: () => { },
  setAuditStats: () => { },
  setRosterIdList: () => { },
  setIsAuditTableLoading: () => { },
  setFlipRefresh: () => { },
  setPlanGroups: () => { },
  setSelectedPlanGroupId: () => { },
  setSelectedSegmentId: () => { }
};

const AuditContext = createContext(initialState);

export function AuditContextProvider({ children }) {
  const [userCurrentRosterId, setUserCurrentRosterId] = useState(initialState.currentRosterId);
  const [userAuditRecordList, setUserAuditRecordList] = useState(initialState.auditRecordList);
  const [userAuditStats, setUserAuditStats] = useState(initialState.auditStats);
  const [userRosterIdList, setUserRosterIdList] = useState(initialState.rosterIdList);
  const [userIsAuditTableLoading, setUserIsAuditTableLoading] = useState(initialState.isAuditTableLoading);
  const [userFlipRefresh, setUserFlipRefresh] = useState(initialState.flipRefresh);
  const [userPlanGroups, setUserPlanGroups] = useState<PlanGroupSummary[]>(initialState.planGroups);
  const [userSelectedPlanGroupId, setUserSelectedPlanGroupId] = useState<string | undefined>(initialState.selectedPlanGroupId);
  const [userSelectedSegmentId, setUserSelectedSegmentId] = useState<string>(initialState.selectedSegmentId);

  function setCurrentRosterId(rosterId) {
    setUserCurrentRosterId(rosterId);
  }

  function setAuditRecordList(auditRecordList) {
    setUserAuditRecordList(auditRecordList);
  }

  function setAuditStats(auditStats) {
    setUserAuditStats(auditStats);
  }

  function setRosterIdList(rosterIdList) {
    setUserRosterIdList(rosterIdList);
  }

  function setIsAuditTableLoading(isAuditTableLoading) {
    setUserIsAuditTableLoading(isAuditTableLoading);
  }

  function setFlipRefresh() {
    setUserFlipRefresh((prevUserRefresh) => !prevUserRefresh);
  }

  function setPlanGroups(planGroups: PlanGroupSummary[]) {
    setUserPlanGroups(planGroups);
  }

  function setSelectedPlanGroupId(id: string | undefined) {
    setUserSelectedPlanGroupId(id);
    setUserSelectedSegmentId(OVERALL_SEGMENT_ID);
  }

  function setSelectedSegmentId(id: string) {
    setUserSelectedSegmentId(id);
  }

  const context: auditStateProps = {
    currentRosterId: userCurrentRosterId,
    auditRecordList: userAuditRecordList,
    auditStats: userAuditStats,
    rosterIdList: userRosterIdList,
    isAuditTableLoading: userIsAuditTableLoading,
    flipRefresh: userFlipRefresh,
    planGroups: userPlanGroups,
    selectedPlanGroupId: userSelectedPlanGroupId,
    selectedSegmentId: userSelectedSegmentId,
    setCurrentRosterId,
    setAuditRecordList,
    setAuditStats,
    setRosterIdList,
    setIsAuditTableLoading,
    setFlipRefresh,
    setPlanGroups,
    setSelectedPlanGroupId,
    setSelectedSegmentId
  };

  return (
    <AuditContext.Provider value={context}>{children}</AuditContext.Provider>
  );
}

export default AuditContext;
